import React, { use } from 'react';
import { GRADIENTS, applyGradientText } from '@/components/ui/styles/gradients';
import { DeviceContext } from '@/hooks/common/DeviceContext';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  bgColor: string;
  description: string;
  className?: string;
  isClickable?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}

const MobileStatCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
  textColor: string;
  mobileTitleStyle: React.CSSProperties;
  cardStyle: React.CSSProperties;
  cardClasses: string;
  isClickable: boolean;
  isInteractiveActive: boolean;
  onClick?: () => void;
}> = ({ title, value, icon, textColor, mobileTitleStyle, cardStyle, cardClasses, isClickable, isInteractiveActive, onClick }) => {
  const Component = isClickable ? 'button' : 'div';
  const titleClass = isInteractiveActive ? 'text-base font-semibold' : 'text-xs font-medium';
  const mobileClass = `${cardClasses} py-2 px-[9px] ${isClickable ? 'cursor-pointer' : ''}`;

  return (
    <Component
      className={mobileClass}
      style={cardStyle}
      type={isClickable ? 'button' : undefined}
      onClick={isClickable ? onClick : undefined}
    >
      <div className="flex flex-col gap-2">
        <p className={`${titleClass} text-left`} style={mobileTitleStyle}>
          {title}
        </p>
        
        <div className="flex items-center justify-between">
          <p className="text-2xl font-bold" style={{ color: textColor }}>
            {value.toLocaleString()}
          </p>
          
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
        </div>
      </div>
    </Component>
  );
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  bgColor,
  description,
  className = '',
  isClickable = false,
  isActive = false,
  onClick
}) => {
  const { isMobile } = use(DeviceContext) ?? { isTablet: false, isMobile: false };
  const isInteractiveActive = isClickable && isActive;

  const textColor = isInteractiveActive ? '#000000' : '#6D6D6D';
  const gradientStyle = isInteractiveActive ? applyGradientText(GRADIENTS.picassoPrimary) : {};
  
  // Apply gradient border on desktop when active
  const cardStyle = isInteractiveActive
    ? {
        background: 'linear-gradient(white, white) padding-box, linear-gradient(to right, #F39A81, #959BEA) border-box',
        border: '2px solid transparent',
      }
    : {};

  const cardClasses = `bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow ${className}`;

  const mobileTitleStyle = isInteractiveActive ? {
    fontFamily: 'Maven Pro',
    fontWeight: 600,
    lineHeight: '100%',
    letterSpacing: '0%',
    color: textColor
  } : {
    color: textColor
  };

  // Mobile layout - horizontal with icon on the right
  if (isMobile) {
    return (
      <MobileStatCard
        title={title}
        value={value}
        icon={icon}
        textColor={textColor}
        mobileTitleStyle={mobileTitleStyle}
        cardStyle={cardStyle}
        cardClasses={cardClasses}
        isClickable={isClickable}
        isInteractiveActive={isInteractiveActive}
        onClick={onClick}
      />
    );
  }

  // Desktop layout - original vertical layout
  return (
    <div
      className={cardClasses}
      style={cardStyle}
    >
      <div className="flex items-start gap-4">
        <div
          className={`w-10 h-10 mt-4 ${
            isInteractiveActive ? 'bg-transparent' : bgColor
          } rounded-lg flex items-center justify-center flex-shrink-0`}
        >
          {icon}
        </div>

        <div className="flex-1 min-w-0 text-left">
          <p
            className={`text-sm font-medium ${
              isInteractiveActive ? '' : 'text-gray-600'
            }`}
            style={gradientStyle}
          >
            {title}
          </p>

          <p
            className={`text-2xl font-semibold mb-1 ${
              isInteractiveActive ? '' : 'text-gray-900'
            }`}
            style={gradientStyle}
          >
            {value.toLocaleString()}
          </p>

          <p className="text-xs text-gray-500">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StatCard;
